import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Info,
  Lock,
  Maximize2,
  Sparkles,
  X,
} from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { Seo, SITE, abs } from "@/components/site/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectsPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index of open screenshot or null

  const gallery = project?.gallery || [];
  const closeLightbox = () => setLightbox(null);
  const showPrev = (e) => {
    e?.stopPropagation();
    setLightbox((i) => (i === null ? i : (i - 1 + gallery.length) % gallery.length));
  };
  const showNext = (e) => {
    e?.stopPropagation();
    setLightbox((i) => (i === null ? i : (i + 1) % gallery.length));
  };

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, gallery.length]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setLoading(true);
    let active = true;
    axios
      .get(`${API}/projects/${projectId}`)
      .then((res) => active && setProject(res.data))
      .catch((e) => console.error("Failed to load project", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [projectId]);

  return (
    <div className="lx-noise relative overflow-hidden">
      {project ? (
        <Seo
          path={`/projekte/${project.id}`}
          title={`${project.name} – ${project.tagline || project.category}`}
          description={project.description || project.long_description}
          image={project.icon}
          type="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: project.name,
            headline: `${project.name} – ${project.tagline || ""}`.trim(),
            description: project.description || project.long_description,
            image: abs(project.icon),
            inLanguage: SITE.lang,
            dateCreated: project.year,
            creator: { "@type": "Organization", name: SITE.name, url: SITE.baseUrl },
            keywords: (project.tags || []).join(", "),
          }}
        />
      ) : (
        !loading && <Seo path="/projekte" title="Projekt nicht gefunden" noindex />
      )}
      <div className="lx-hero-ambient absolute inset-0 -z-10 opacity-70" />
      <div className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32 lg:px-8 lg:pb-28 lg:pt-40">
        <Link
          to="/#projekte"
          data-testid="projects-back-link"
          className="lx-navlink inline-flex items-center gap-2 font-mono-lx text-xs uppercase tracking-[0.18em]"
        >
          <ArrowLeft size={14} /> Zurück zur Startseite
        </Link>

        {loading ? (
          <div className="mt-10 lx-card h-96 animate-pulse" style={{ opacity: 0.5 }} />
        ) : !project ? (
          <p className="mt-10 text-[color:var(--lx-text-2)]">
            Projekt konnte nicht geladen werden.
          </p>
        ) : (
          <>
            {/* Header */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14"
            >
              <div className="lg:col-span-12">
                {project.icon && (
                  <motion.div variants={fadeUp} className="mb-5">
                    <div
                      className="h-20 w-20 overflow-hidden rounded-[22px] sm:h-24 sm:w-24 sm:rounded-[26px]"
                      style={{
                        border: "1px solid rgba(214,255,226,0.14)",
                        boxShadow: "0 10px 26px rgba(0,0,0,0.45)",
                      }}
                    >
                      <img
                        src={project.icon}
                        alt={`${project.name} App-Icon`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </motion.div>
                )}
                <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3">
                  <span
                    className="rounded-full border px-3 py-1 font-mono-lx text-[11px] uppercase tracking-wider"
                    style={{
                      borderColor: "rgba(168,232,92,0.3)",
                      color: "var(--lx-green-soft)",
                    }}
                  >
                    {project.category}
                  </span>
                  <span className="font-mono-lx text-[11px]" style={{ color: "var(--lx-muted)" }}>
                    {project.year} · {project.status}
                  </span>
                </motion.div>

                <motion.h1
                  variants={fadeUp}
                  className="mt-5 font-display text-4xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-5xl"
                >
                  {project.name}
                </motion.h1>
                <motion.p
                  variants={fadeUp}
                  className="mt-3 text-lg"
                  style={{ color: "var(--lx-green-soft)" }}
                >
                  {project.tagline}
                </motion.p>
                <motion.p
                  variants={fadeUp}
                  className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--lx-text-2)]"
                >
                  {project.long_description}
                </motion.p>

                <motion.div variants={fadeUp} className="mt-6 flex flex-wrap gap-2">
                  {(project.tags || []).map((t) => (
                    <span
                      key={t}
                      className="rounded-full border px-3 py-1 font-mono-lx text-[11px]"
                      style={{ borderColor: "var(--lx-border)", color: "var(--lx-muted)" }}
                    >
                      {t}
                    </span>
                  ))}
                </motion.div>

                {project.url && (
                  <motion.div variants={fadeUp} className="mt-7">
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="project-live-link"
                      className="group inline-flex items-center gap-2 rounded-[12px] px-5 py-3 font-display font-medium"
                      style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
                    >
                      Zur Live-Website
                      <ExternalLink size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Confidential notice (only when set) */}
            {project.confidential_notice && (
              <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                data-testid="project-confidential-notice"
                className="mt-12 sm:mt-14"
              >
                <div
                  className="lx-terminal flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:gap-5 sm:p-7"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(168,232,92,0.06), rgba(14,20,16,0.9))",
                    borderColor: "rgba(168,232,92,0.28)",
                  }}
                >
                  <span
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px]"
                    style={{ background: "rgba(168,232,92,0.12)", color: "var(--lx-green)" }}
                  >
                    <Lock size={20} />
                  </span>
                  <div className="flex-1">
                    <div className="lx-kicker mb-1.5">Auskunft auf Anfrage</div>
                    <p className="text-sm leading-relaxed text-[color:var(--lx-text-2)] sm:text-[15px]">
                      {project.confidential_notice}
                    </p>
                  </div>
                  <a
                    href="/#kontakt"
                    data-testid="project-confidential-cta"
                    className="group inline-flex shrink-0 items-center gap-2 rounded-[12px] px-5 py-3 font-display font-medium"
                    style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
                  >
                    Anfrage stellen
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                  </a>
                </div>
              </motion.section>
            )}

            {/* Gallery (only when screenshots exist) */}
            {project.gallery && project.gallery.length > 0 && (
              <section className="mt-16 sm:mt-20">
                <div className="lx-kicker mb-3">Einblicke</div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
                  Ein Blick ins Produkt.
                </h2>
                {project.gallery_note && (
                  <p
                    data-testid="project-gallery-note"
                    className="mt-3 inline-flex items-start gap-2 rounded-[10px] border px-3.5 py-2.5 text-[13px] leading-relaxed text-[color:var(--lx-text-2)]"
                    style={{ borderColor: "var(--lx-border)", background: "var(--lx-surface-2)" }}
                  >
                    <Info size={15} className="mt-0.5 shrink-0" style={{ color: "var(--lx-green)" }} />
                    <span>{project.gallery_note}</span>
                  </p>
                )}
                <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {project.gallery.map((src, i) => (
                    <motion.button
                      type="button"
                      key={i}
                      onClick={() => setLightbox(i)}
                      data-testid="project-screenshot"
                      aria-label={`${project.name} Screenshot ${i + 1} vergrössern`}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={viewportOnce}
                      transition={{ delay: i * 0.06, duration: 0.45 }}
                      className="group relative block cursor-zoom-in overflow-hidden rounded-[16px] border"
                      style={{
                        borderColor: "var(--lx-border-strong)",
                        boxShadow: "var(--lx-shadow-soft)",
                      }}
                    >
                      <img
                        src={src}
                        alt={`${project.name} Screenshot ${i + 1}`}
                        loading="lazy"
                        className="h-auto w-full transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                      <span
                        className="pointer-events-none absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                        style={{ background: "rgba(7,10,8,0.7)", color: "var(--lx-green)", backdropFilter: "blur(6px)" }}
                      >
                        <Maximize2 size={16} />
                      </span>
                    </motion.button>
                  ))}
                </div>
              </section>
            )}


            {/* Features */}
            <section className="mt-16 sm:mt-20">
              <div className="lx-kicker mb-3">Funktionen</div>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
                Was das Projekt besonders macht.
              </h2>
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2"
              >
                {(project.features || []).map((f) => (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    data-testid="project-feature"
                    className="lx-card flex gap-4 p-6"
                  >
                    <span
                      className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-[10px]"
                      style={{ background: "rgba(168,232,92,0.12)", color: "var(--lx-green)" }}
                    >
                      <Check size={16} />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-semibold text-[color:var(--lx-text)]">
                        {f.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--lx-text-2)]">
                        {f.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </section>

            {/* CTA */}
            <section className="mt-16 sm:mt-20">
              <div
                className="lx-terminal p-8 text-center sm:p-12"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(168,232,92,0.07), rgba(14,20,16,0.9))",
                  borderColor: "rgba(168,232,92,0.28)",
                }}
              >
                <Sparkles size={26} className="mx-auto" style={{ color: "var(--lx-green)" }} />
                <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl">
                  Deine App könnte die nächste sein.
                </h2>
                <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-[color:var(--lx-text-2)] sm:text-base">
                  Erzähl uns von deiner Idee, transparent, mit 100% Geld-zurück-Garantie und laufenden Updates.
                </p>
                <Link
                  to="/#kontakt"
                  data-testid="projects-cta"
                  className="group mt-6 inline-flex items-center gap-2 rounded-[12px] px-6 py-3 font-display font-medium"
                  style={{ background: "var(--lx-green)", color: "var(--lx-canvas)" }}
                >
                  Projekt anfragen
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </section>
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && gallery[lightbox] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeLightbox}
            data-testid="lightbox-overlay"
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
            style={{ background: "rgba(5,7,6,0.9)", backdropFilter: "blur(14px)" }}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={closeLightbox}
              data-testid="lightbox-close"
              aria-label="Schliessen"
              className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full border transition-colors hover:bg-[color:var(--lx-surface)]"
              style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-text)", background: "rgba(14,20,16,0.6)" }}
            >
              <X size={20} />
            </button>

            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPrev}
                  aria-label="Vorheriges Bild"
                  className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border transition-colors hover:bg-[color:var(--lx-surface)] sm:left-6"
                  style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-text)", background: "rgba(14,20,16,0.6)" }}
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={showNext}
                  aria-label="Nächstes Bild"
                  className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border transition-colors hover:bg-[color:var(--lx-surface)] sm:right-6"
                  style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-text)", background: "rgba(14,20,16,0.6)" }}
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}

            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              src={gallery[lightbox]}
              alt={`${project?.name} Screenshot ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
              className="max-h-[88vh] max-w-[92vw] rounded-[14px] border object-contain"
              style={{ borderColor: "var(--lx-border-strong)", boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
            />

            {gallery.length > 1 && (
              <div
                className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full border px-3 py-1 font-mono-lx text-xs"
                style={{ borderColor: "var(--lx-border)", color: "var(--lx-text-2)", background: "rgba(14,20,16,0.7)" }}
              >
                {lightbox + 1} / {gallery.length}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
